import React, { useState, useEffect, useCallback,useMemo } from 'react';
import Select from 'react-select';
import isEmail from 'is-email';
import { CountryDropdown, RegionDropdown } from 'react-country-region-selector';
import * as AccessLevels from '@/components/General/AccessLevels';
import AddUsers from '@/components/AccountSetup/AddUsers';
import AdminCredentials from './AdminCredentials';
import CompanyPurchasesReport from '@/components/CompanyPurchasesReport/CompanyPurchasesReport';
import ConfigureProducts from '@/components/AccountSetup/ConfigureProducts';
import PlaceOrder from '@/components/AccountSetup/PlaceOrder';
import TextInput from '@/components/General/TextInput';
import generalStyles from '@/components/General/Styles.module.scss';
import { handleApiError } from '@/components/General/HandleApiError';
import { createNewCompany, updateCompany, getAllCompanyTypes } from '@/services/api/company.service';
import API_STATES from '@/constants/StateConstants';
import store from '../../store';
import styles from './AccountSetup.module.scss';

function CompanyDetails(props) {
    const [companyForm, setCompanyForm] = useState({
        companyName: '',
        companyDomain: '',
        alternateDomain1: '',
        alternateDomain2: '',
        contact: '',
        email: '',
        upliftPercent: '',
        accessLevel: {
            label: 'Manufacturer Hidden Prices',
            value: 'manufacturer_hidden_prices',
        },
        creditLimit: 350000,
        companyType: {},
    });

    const [addressForm, setAddressForm] = useState({
        line1: '',
        line2: '',
        city: '',
        state: '',
        countryCode: 'US',
        zipcode: '',
    });

    const [validation, setValidation] = useState({
        isCompanyDomainValid: true,
        isAlternateDomain1Valid: true,
        isAlternateDomain2Valid: true,
        isValid: true,
        zipcodeValid: true,
        percentValid: true,
        creditLimitValid: true,
    });

    const [apiState, setApiState] = useState({
        companyType: API_STATES.none,
        companyTypeError: '',
        newAccount: API_STATES.none,
        newAccountError: '',
        updateAccount: API_STATES.none,
        updateAccountError: '',
    });

    const [uiState, setUiState] = useState({
        companyTypeList: [],
        hasDomains: {
            alternateDomain1: false,
            alternateDomain2: false,
        },
        createOrder: false,
        createCompany: false,
        nsCompanyId: 0,
        showCompanyProductsConfig: false,
        newAdminAdded: false,
        companyHasPurchase: false,
        deleteAdminManual: false,
        purchasesAndProductsRefreshParams: {},
    });

    const [adminState, setAdminState] = useState({
        username: null,
        password: null,
        userId: 0,
        deleted: false,
    });

    const isSuperUser = store?.getState()?.account?.isSuperUser;

    function isValidDomain(domain) {
        return /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/.test(domain);
    }

    useEffect(() => {
        const { alternateDomain1, alternateDomain2, contact, upliftPercent, companyDomain, creditLimit } = companyForm;
        const { zipcode } = addressForm;

        const newValidation = { ...validation };
        let hasChanges = false;

        const altDom1Valid = alternateDomain1 ? isValidDomain(alternateDomain1) : true;
        if (newValidation.isAlternateDomain1Valid !== altDom1Valid) {
            newValidation.isAlternateDomain1Valid = altDom1Valid;
            hasChanges = true;
        }

        const altDom2Valid = alternateDomain2 ? isValidDomain(alternateDomain2) : true;
        if (newValidation.isAlternateDomain2Valid !== altDom2Valid) {
            newValidation.isAlternateDomain2Valid = altDom2Valid;
            hasChanges = true;
        }

        let contactValid = true;
        if (contact !== '') {
            let isnum = /^\d+$/.test(String(contact));
            let len = String(contact).length === 10;
            contactValid = isnum && len;
        }
        if (newValidation.isValid !== contactValid) {
            newValidation.isValid = contactValid;
            hasChanges = true;
        }

        let zipValid = true;
        if (zipcode !== '') {
            zipValid = /^[a-zA-Z0-9]+$/.test(zipcode);
        }
        if (newValidation.zipcodeValid !== zipValid) {
            newValidation.zipcodeValid = zipValid;
            hasChanges = true;
        }

        let perValid = true;
        if (upliftPercent !== '') {
            perValid = /^[+-]?\d+(\.\d+)?$/.test(upliftPercent);
        }
        if (newValidation.percentValid !== perValid) {
            newValidation.percentValid = perValid;
            hasChanges = true;
        }

        let compDomValid = true;
        if (companyDomain !== '') {
            compDomValid = isValidDomain(companyDomain);
        }
        if (newValidation.isCompanyDomainValid !== compDomValid) {
            newValidation.isCompanyDomainValid = compDomValid;
            hasChanges = true;
        }

        let credValid = true;
        if (creditLimit !== '') {
            credValid = /^\d+$/.test(creditLimit);
        }
        if (newValidation.creditLimitValid !== credValid) {
            newValidation.creditLimitValid = credValid;
            hasChanges = true;
        }

        if (hasChanges) {
            setValidation(newValidation);
        }
    }, [companyForm, addressForm]);

    const isUpdateDisabled = useMemo(() =>
        (!validation.isCompanyDomainValid ||
        !validation.isAlternateDomain1Valid ||
        !validation.isAlternateDomain2Valid ||
        !validation.creditLimitValid ||
        !validation.zipcodeValid ||
        !validation.percentValid ||
        !isEmail(companyForm.email) ||
        !validation.isValid)
    , [validation, companyForm.email]);

    const handleCompanyChange = (field, value) => { 
        setCompanyForm(prev => ({ ...prev, [field]: value }));
    };

    const handleAddressChange = (field, value) => {
        setAddressForm(prev => ({ ...prev, [field]: value }));
    };

    const onInternalAdminCreated = useCallback(() => {
        setUiState(prev => ({ ...prev, newAdminAdded: true }));
        refreshPurchasesAndProducts();
    }, []);

    const refreshPurchasesAndProducts = (refreshDelay = 0) => {
        setUiState(prev => ({
            ...prev,
            showCompanyProductsConfig: true,
            purchasesAndProductsRefreshParams: { refreshTimestamp: Date.now(), refreshDelay }
        }));
    };

    const onPurchasesUpdated = useCallback(() => {
        refreshPurchasesAndProducts(100000);
    }, []);

    useEffect(() => {
        getCompanyTypeDetails();
    }, []);

    useEffect(() => {
        // Automatically set access level based on company type
        if (companyForm.companyType?.value === 6) {
            setCompanyForm(prev => ({
                ...prev,
                accessLevel: {
                    label: 'All Else',
                    value: 'all_else',
                }
            }));
        }
    }, [companyForm.companyType]);

    useEffect(() => {
        if (props.companyInfo?.nsCompanyId) {
            const info = props.companyInfo;
            setUiState(prev => ({
                ...prev,
                nsCompanyId: info.nsCompanyId,
                hasDomains: {
                    alternateDomain1: Boolean(info.alternateDomains?.[0]),
                    alternateDomain2: Boolean(info.alternateDomains?.[1]),
                },
                showCompanyProductsConfig: true,
                companyHasPurchase: false,
                deleteAdminManual: false,
            }));

            setCompanyForm(prev => ({
                ...prev,
                companyName: info.companyName,
                companyDomain: info.companyDomain,
                alternateDomain1: info.alternateDomains?.[0] || '',
                alternateDomain2: info.alternateDomains?.[1] || '',
                companyType: info.companyType,
                contact: info.contact ?? '',
                email: info.email ?? '',
                accessLevel: info.accessLevel,
                creditLimit: info.creditLimit,
                upliftPercent: info.upliftPercentage * 100,
            }));

            setAddressForm({
                line1: info.address?.addr1,
                line2: info.address?.addr2,
                city: info.address?.city ?? '',
                state: info.address?.state ?? '',
                countryCode: info.address?.countryCode ?? '',
                zipcode: info.address?.zipcode,
            });

            setAdminState({
                username: info.adminUsername,
                password: info.adminPassword,
                userId: info.adminUserId,
                deleted: info.isAdminDeleted,
            });
        }
    }, [props.companyInfo]);

    const isSubmitEnabled = () => {
        return (
            companyForm.companyName !== '' &&
            companyForm.companyDomain !== '' &&
            validation.isCompanyDomainValid &&
            validation.isAlternateDomain1Valid &&
            validation.isAlternateDomain2Valid &&
            companyForm.companyType?.value &&
            apiState.newAccount !== API_STATES.loading &&
            uiState.createOrder !== true &&
            addressForm.line1 &&
            isEmail(companyForm.email) &&
            companyForm.contact &&
            addressForm.city &&
            addressForm.state &&
            addressForm.countryCode &&
            addressForm.zipcode &&
            validation.isValid &&
            validation.zipcodeValid &&
            companyForm.creditLimit &&
            validation.creditLimitValid &&
            validation.percentValid
        );
    };

    async function getCompanyTypeDetails() {
        setApiState(prev => ({ ...prev, companyType: API_STATES.loading }));
        const response = await getAllCompanyTypes();
        switch (response.status) {
            case API_STATES.success:
                const data = response.data.map((row) => {
                    return {
                        label: row.display,
                        value: row.value,
                    };
                });
                setUiState(prev => ({ ...prev, companyTypeList: data }));
                setApiState(prev => ({ ...prev, companyType: API_STATES.success }));
                break;
            case API_STATES.error:
                let error = response.error;
                handleApiError(error,
                    (state) => setApiState(prev => ({ ...prev, companyType: state })),
                    (msg) => setApiState(prev => ({ ...prev, companyTypeError: msg }))
                );
                break;
            default:
                setApiState(prev => ({ ...prev, companyTypeError: '', companyType: API_STATES.none }));
        }
    }

    async function onCreateHandler() {
        setApiState(prev => ({ ...prev, newAccount: API_STATES.loading }));
        let alternateDomains = [];
        let createdDomain1 = false;
        let createdDomain2 = false;
        if (companyForm.alternateDomain1 && validation.isAlternateDomain1Valid) {
            alternateDomains.push(companyForm.alternateDomain1);
            createdDomain1 = true;
        }
        if (companyForm.alternateDomain2 && validation.isAlternateDomain2Valid) {
            alternateDomains.push(companyForm.alternateDomain2);
            createdDomain2 = true;
        }
        const response = await createNewCompany(
            companyForm.companyName,
            companyForm.companyType,
            companyForm.companyDomain,
            alternateDomains,
            companyForm.email,
            companyForm.contact,
            Number(companyForm.creditLimit),
            {
                street1: addressForm.line1,
                street2: addressForm.line2,
                city: addressForm.city,
                state: addressForm.state,
                countryCode: addressForm.countryCode,
                zipcode: addressForm.zipcode,
            },
            Number(companyForm.upliftPercent),
            companyForm.accessLevel?.value
        );
        switch (response.status) {
            case API_STATES.success:
                setUiState(prev => ({
                    ...prev,
                    hasDomains: {
                        alternateDomain1: createdDomain1,
                        alternateDomain2: createdDomain2,
                    },
                    createOrder: true,
                    createCompany: true,
                    nsCompanyId: response.data?.nsCompanyId,
                }));
                setApiState(prev => ({ ...prev, newAccount: API_STATES.success }));
                setTimeout(() => setApiState(prev => ({ ...prev, newAccount: API_STATES.none })), 3000);
                break;
            case API_STATES.error:
                let error = response.error;
                handleApiError(error,
                    (state) => setApiState(prev => ({ ...prev, newAccount: state })),
                    (msg) => setApiState(prev => ({ ...prev, newAccountError: msg }))
                );
                break;
            default:
                setApiState(prev => ({ ...prev, newAccountError: '', newAccount: API_STATES.none }));
        }
    }

    async function onUpdateHandler() {
        setApiState(prev => ({ ...prev, updateAccount: API_STATES.loading }));
        let addedDomains = [];
        let updatedDomain1 = false;
        let updatedDomain2 = false;
        if (!uiState.hasDomains?.alternateDomain1 && companyForm.alternateDomain1 && validation.isAlternateDomain1Valid) {
            addedDomains.push(companyForm.alternateDomain1);
            updatedDomain1 = true;
        }
        if (!uiState.hasDomains?.alternateDomain2 && companyForm.alternateDomain2 && validation.isAlternateDomain2Valid) {
            addedDomains.push(companyForm.alternateDomain2);
            updatedDomain2 = true;
        }
        const response = await updateCompany(
            props.companyInfo?.nsCompanyId ? props.companyInfo?.nsCompanyId : uiState.nsCompanyId,
            addedDomains,
            companyForm.email,
            companyForm.contact,
            Number(companyForm.upliftPercent),
            {
                street1: addressForm.line1,
                street2: addressForm.line2,
                city: addressForm.city,
                state: addressForm.state,
                countryCode: addressForm.countryCode,
                zipcode: addressForm.zipcode,
            },
            String(companyForm.accessLevel?.value)
        );
        switch (response.status) {
            case API_STATES.success:
                setUiState(prev => ({
                    ...prev,
                    hasDomains: {
                        alternateDomain1: prev.hasDomains?.alternateDomain1 || updatedDomain1,
                        alternateDomain2: prev.hasDomains?.alternateDomain2 || updatedDomain2,
                    }
                }));
                setApiState(prev => ({ ...prev, updateAccount: API_STATES.success }));
                setTimeout(() => setApiState(prev => ({ ...prev, updateAccount: API_STATES.none })), 3000);
                break;
            case API_STATES.error:
                let error = response.error;
                handleApiError(error,
                    (state) => setApiState(prev => ({ ...prev, updateAccount: state })),
                    (msg) => setApiState(prev => ({ ...prev, updateAccountError: msg }))
                );
                break;
            default:
                setApiState(prev => ({ ...prev, updateAccountError: '', updateAccount: API_STATES.none }));
        }
    }

    return (
        <>
            <h3 className={styles.screenHeading}>Step 1 of 5: Company Details</h3>
            <hr />

            <div className={styles.rowflex}>
                {/*First div section of company form*/}
                <div>
                    <TextInput
                        type="text"
                        label="Company Name:"
                        placeholder="Company Name"
                        value={companyForm.companyName}
                        onInputChange={(e) => handleCompanyChange('companyName', e.target.value)}
                        disabled={props.companyInfo?.nsCompanyId || uiState.createOrder}
                        required={true}
                    />

                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            name="companyDomain"
                            label="Company Domain:"
                            placeholder="Company Domain"
                            value={companyForm.companyDomain}
                            onInputChange={(e) => handleCompanyChange('companyDomain', e.target.value)}
                            disabled={props.companyInfo?.nsCompanyId || uiState.createOrder}
                            required={true}
                        />
                        {!validation.isCompanyDomainValid && <span className={generalStyles.errorMessage}>Invalid Domain!</span>}
                    </div>

                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            name="alternateDomain"
                            label="Alternate Domain 1:"
                            placeholder="Alternate Domain"
                            value={companyForm.alternateDomain1}
                            onInputChange={(e) => handleCompanyChange('alternateDomain1', e.target.value)}
                            disabled={uiState.hasDomains?.alternateDomain1}
                        />
                        {!validation.isAlternateDomain1Valid && (
                            <span className={generalStyles.errorMessage}>Invalid Domain!</span>
                        )}
                    </div>
                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            name="alternateDomain2"
                            label="Alternate Domain 2:"
                            placeholder="Alternate Domain 2"
                            value={companyForm.alternateDomain2}
                            onInputChange={(e) => handleCompanyChange('alternateDomain2', e.target.value)}
                            disabled={uiState.hasDomains?.alternateDomain2}
                        />
                        {!validation.isAlternateDomain2Valid && (
                            <span className={generalStyles.errorMessage}>Invalid Domain!</span>
                        )}
                    </div>
                    {apiState.companyType === API_STATES.loading && (
                        <label className={generalStyles.statusMessage}>Loading...</label>
                    )}
                    {apiState.companyType === API_STATES.error && (
                        <label className={generalStyles.errorMessage}>{apiState.companyTypeError}</label>
                    )}
                    {apiState.companyType === API_STATES.success && (
                        <div className={generalStyles['input-wrapper']}>
                            <label className={styles.required}>Company Type:</label>
                            <span className={styles.formField}>
                                <Select
                                    classNamePrefix={'react-select'}
                                    onChange={(type) => handleCompanyChange('companyType', type)}
                                    options={uiState.companyTypeList}
                                    value={companyForm.companyType}
                                    isDisabled={props.companyInfo?.nsCompanyId || uiState.createOrder}
                                />
                            </span>
                        </div>
                    )}

                    <div className={generalStyles['input-wrapper']}>
                        <label className={styles.required}>Access Level:</label>
                        <span className={styles.formField}>
                            <Select
                                classNamePrefix={'react-select'}
                                onChange={(type) => handleCompanyChange('accessLevel', type)}
                                options={AccessLevels.AccessLevels}
                                value={companyForm.accessLevel}
                            />
                        </span>
                    </div>

                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            label="Contact Number:"
                            placeholder="Contact Number"
                            value={companyForm.contact}
                            onInputChange={(event) => handleCompanyChange('contact', event.target.value)}
                            required={true}
                        />
                        {!validation.isValid && <span className={generalStyles.errorMessage}>Invalid phone Number!</span>}
                    </div>

                    <div className={styles.formItem}>
                        <TextInput
                            type="email"
                            label="Email:"
                            placeholder="Email"
                            value={companyForm.email}
                            onInputChange={(event) => handleCompanyChange('email', event.target.value)}
                            required={true}
                        />
                        {companyForm.email && !isEmail(companyForm.email) && (
                            <span className={generalStyles.errorMessage}>Invalid email format!</span>
                        )}
                    </div>

                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            label="Uplift %:"
                            placeholder="Uplift %"
                            value={companyForm.upliftPercent}
                            onInputChange={(event) => handleCompanyChange('upliftPercent', event.target.value)}
                        />
                        {!validation.percentValid && <span className={generalStyles.errorMessage}>Invalid percent value!</span>}
                    </div>
                </div>

                {/*Second div section of company form*/}
                <div>
                    <TextInput
                        type="text"
                        label="Address 1:"
                        placeholder="Address 1"
                        value={addressForm.line1}
                        onInputChange={(event) => handleAddressChange('line1', event.target.value)}
                        required={true}
                    />

                    <TextInput
                        type="text"
                        label="Address 2:"
                        placeholder="Address 2"
                        value={addressForm.line2}
                        onInputChange={(event) => handleAddressChange('line2', event.target.value)}
                    />

                    <TextInput
                        type="text"
                        label="City:"
                        placeholder="City"
                        value={addressForm.city}
                        onInputChange={(event) => handleAddressChange('city', event.target.value)}
                        required={true}
                    />

                    <div className={generalStyles['input-wrapper']}>
                        <label className={styles.required}>Country:</label>
                        <span className={styles.formField}>
                            <CountryDropdown
                                defaultOptionLabel=""
                                valueType="short"
                                classes={`${generalStyles.input} ${styles.countryStateDropdown}`}
                                value={addressForm.countryCode}
                                onChange={(val) => handleAddressChange('countryCode', val)} />
                        </span>
                    </div>
                    <div className={generalStyles['input-wrapper']}>
                        <label className={styles.required}>State/Territory:</label>
                        <span className={styles.formField}>
                            <RegionDropdown
                                countryValueType="short"
                                defaultOptionLabel=''
                                valueType="short"
                                classes={`${generalStyles.input} ${styles.countryStateDropdown}`}
                                country={addressForm.countryCode}
                                value={addressForm.state}
                                onChange={(val) => handleAddressChange('state', val)} />
                        </span>
                    </div>
                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            label="Zip Code:"
                            placeholder="Zip Code"
                            value={addressForm.zipcode}
                            onInputChange={(event) => handleAddressChange('zipcode', event.target.value)}
                            required={true}
                        />
                        {!validation.zipcodeValid && <span className={generalStyles.errorMessage}>Invalid zipcode!</span>}
                    </div>

                    <div className={styles.formItem}>
                        <TextInput
                            type="text"
                            label="Credit Limit:"
                            placeholder="Credit Limit"
                            value={companyForm.creditLimit}
                            onInputChange={(event) => handleCompanyChange('creditLimit', event.target.value)}
                            required={true}
                            disabled={true}
                        />
                        {!validation.creditLimitValid && <span className={generalStyles.errorMessage}>Invalid creditLimit!</span>}
                    </div>
                    <br />
                    <button
                        className={generalStyles.submitButton}
                        onClick={onCreateHandler}
                        disabled={!isSubmitEnabled() || props.companyInfo?.nsCompanyId}
                    >
                        Create Company
                    </button>
                    <span>
                        {apiState.newAccount === API_STATES.error && (
                            <label className={generalStyles.errorMessage}>{apiState.newAccountError}</label>
                        )}
                        {apiState.newAccount === API_STATES.loading && (
                            <label className={generalStyles.statusMessage}>
                                Please wait as this might take a little while...
                            </label>
                        )}
                        {apiState.newAccount === API_STATES.success && (
                            <>
                                <label className={generalStyles.successMessage}>
                                    Successfully created new account.
                                </label>
                            </>
                        )}
                    </span>
                    <button
                        className={generalStyles.submitButton}
                        onClick={onUpdateHandler}
                        disabled={isUpdateDisabled || (!props.companyInfo?.nsCompanyId && !uiState.createOrder)}
                    >
                        Update Company
                    </button>
                    <span>
                        {apiState.updateAccount === API_STATES.error && (
                            <label className={generalStyles.errorMessage}>{apiState.updateAccountError}</label>
                        )}
                        {apiState.updateAccount === API_STATES.loading && (
                            <label className={generalStyles.statusMessage}>
                                Please wait as this might take a little while...
                            </label>
                        )}
                        {apiState.updateAccount === API_STATES.success && (
                            <>
                                <label className={generalStyles.successMessage}>Successfully Updated!</label>
                            </>
                        )}
                    </span>
                </div>
            </div>
            {(props.companyInfo?.nsCompanyId || uiState.createCompany) && (
                <>
                    <AdminCredentials
                        adminUsername={adminState.username}
                        adminPassword={adminState.password}
                        adminUserId={adminState.userId}
                        adminCreated={onInternalAdminCreated}
                        companyName={companyForm.companyName}
                        nsCompanyId={uiState.nsCompanyId}
                        adminDeleted={adminState.deleted}
                        onDeleteAdminManual={() => setUiState(prev => ({ ...prev, deleteAdminManual: true }))}
                    />
                    <h3 className={styles.screenHeading}>Step 3 of 5: Place Order</h3>
                    <hr />
                    <CompanyPurchasesReport
                        nsCompanyId={uiState.nsCompanyId}
                        refreshTimestamp={uiState.purchasesAndProductsRefreshParams.refreshTimestamp}
                        refreshDelay={uiState.purchasesAndProductsRefreshParams.refreshDelay}
                        someCompanyPurchase={() => setUiState(prev => ({ ...prev, companyHasPurchase: true }))}
                        noCompanyPurchase={() => setUiState(prev => ({ ...prev, companyHasPurchase: false }))}
                        isEditable={isSuperUser}
                    />
                    <br />
                    <PlaceOrder
                        nsCompanyId={uiState.nsCompanyId}
                        updatedPurchases={onPurchasesUpdated}
                        companyInfo={props.companyInfo}
                        resetCreateOrder={() => setUiState(prev => ({ ...prev, createCompany: false }))}
                        companyHasPurchase={uiState.companyHasPurchase}
                    />
                    <br />
                    {uiState.showCompanyProductsConfig && (
                        <>
                            <h3 className={styles.screenHeading}>Step 4 of 5: Configure Products</h3>
                            <hr />
                            <ConfigureProducts
                                nsCompanyId={uiState.nsCompanyId}
                                refreshTimestamp={uiState.purchasesAndProductsRefreshParams.refreshTimestamp}
                                refreshDelay={uiState.purchasesAndProductsRefreshParams.refreshDelay}
                            />
                            <h3 className={styles.screenHeading}>Step 5 of 5: Invite Users</h3>
                            <hr />
                            <AddUsers
                                nsCompanyId={uiState.nsCompanyId}
                                adminCreated={uiState.newAdminAdded}
                                deleteAdminManual={uiState.deleteAdminManual}
                            />
                        </>
                    )}
                </>
            )}
        </>
    );
}
export default CompanyDetails;
